import tinycolor from 'tinycolor2';

const Color = tinycolor;

export class ColorHelper {
  /**
   * Generate color variations
   * @param {string} colorValue Color base to variations
   * @param {string} customContrast Color to contrast calc (default: black/white)
   * @param {number} alpha Alpha color to to hover and active (0.0~1.0)
   * @param {number} hover Percent to mix color with contrast to hover (0~100)
   * @param {number} active Percent to mix color with contrast to active (0~100)
   * @returns {{name:string, value: string}[]} [{name:'contrast', value: '#ffffff'}, {name: 'hover', value: '#333333ff'}, {name: 'active', value: '#4d4d4dff'}]
   */
  static getColorVariations(
    colorValue: string,
    customContrast?: string,
    alpha = 1,
    hover = 20,
    active = 30,
  ): { name: string; value: string }[] {
    const color = Color(colorValue);
    const contrast = customContrast || this.getContrastColor(colorValue);
    return [
      {
        name: 'contrast',
        value: contrast,
      },
      {
        name: 'hover',
        value: Color.mix(color, contrast, hover).setAlpha(alpha).toHex8String(),
      },
      {
        name: 'active',
        value: Color.mix(color, contrast, active).setAlpha(alpha).toHex8String(),
      },
    ];
  }
  /**
   * Generate light and dark variations
   * @param {string} colorValue Color base to variations
   * @param {number} light Percent to mix color with contrast to light (0~100)
   * @param {number} dark Percent to mix color with contrast to dark (0~100)
   * @returns {{name:string, value: string}[]} [{name:'light', value: '#ffffff'}, {name: 'dark', value: '#000000'}]
   */
  static getSurfaceColorVariations(colorValue: string, light = 96, dark = 92): { name: string; value: string }[] {
    const color = Color(colorValue);
    return [
      {
        name: 'light',
        value: Color.mix(color, '#ffffff', light).toHexString(),
      },
      {
        name: 'dark',
        value: Color.mix(color, '#ffffff', dark).toHexString(),
      },
    ];
  }

  /**
   * Generate medium and low variations
   * @param {string} colorValue Color base to variations
   * @param {number} medium Percent to mix color with contrast to medium (0~100)
   * @param {number} low Percent to mix color with contrast to low (0~100)
   * @returns {{name:string, value: string}[]} [{name:'medium', value: '#ffffff'}, {name: 'low', value: '#000000'}, name: 'inverse', value: '#000000'}]
   */
  static getForegroundColorVariations(colorValue: string, medium = 33, low = 48): { name: string; value: string }[] {
    const color = Color(colorValue);
    const contrast = this.getContrastColor(colorValue);
    return [
      {
        name: 'medium',
        value: Color.mix(color, '#ffffff', medium).toHexString(),
      },
      {
        name: 'low',
        value: Color.mix(color, '#ffffff', low).toHexString(),
      },
      {
        name: 'inverse',
        value: contrast,
      },
    ];
  }

  static getTintOrShadeColor(colorValue: string): string {
    const color = Color(colorValue);
    const contrast = this.getContrastColor(colorValue);
    return Color.mix(color, contrast, 50).toHexString();
  }

  /**
   * Return best contrast color (black or white)
   * @param {string} colorValue
   */
  static getContrastColor(colorValue: string): '#000000' | '#FFFFFF' {
    if (Color.isReadable(colorValue, 'white', {})) {
      return '#FFFFFF';
    }
    return '#000000';
  }
}
